import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Twitter, Linkedin, Github, Youtube, Shield, Lock, CheckCircle, Globe, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Logo from './Logo';

const linkGroups = [
  {
    title: 'Product',
    links: [
      { label: 'EvalentX Arena', to: '/codearena', testid: 'arena' },
      { label: 'Mock Interviews', to: '/codearena/mock-interview', testid: 'mock-interviews' },
      { label: 'Practice Problems', to: '/codearena/problems', testid: 'practice' },
      { label: 'Live Contests', to: '/codearena/contests', testid: 'contests' },
      { label: 'Verified Profile', to: '/codearena/verified-talent', testid: 'verified' },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { label: 'For Companies', to: '/company', testid: 'for-companies' },
      { label: 'For Colleges', to: '/colleges', testid: 'for-colleges' },
      { label: 'For Developers', to: '/codearena/auth', testid: 'for-developers' },
      { label: 'Hiring Challenges', to: '/company/hiring-challenges', testid: 'hiring-challenges' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about', testid: 'about' },
      { label: 'Customers', to: '/customers', testid: 'customers' },
      { label: 'Careers', to: '/careers', testid: 'careers' },
      { label: 'Press', to: '/press', testid: 'press' },
      { label: 'Contact', to: '/contact', testid: 'contact' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Blog', to: '/blog', testid: 'blog' },
      { label: 'Docs', to: '/docs', testid: 'docs' },
      { label: 'Help Center', to: '/help', testid: 'help' },
      { label: 'API', to: '/api-docs', testid: 'api' },
      { label: 'Status', to: '/status', testid: 'status' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy', to: '/privacy', testid: 'privacy' },
      { label: 'Terms', to: '/terms', testid: 'terms' },
      { label: 'Security', to: '/security', testid: 'security' },
      { label: 'Cookies', to: '/cookies', testid: 'cookies' },
      { label: 'GDPR', to: '/gdpr', testid: 'gdpr' },
    ],
  },
];

const socials = [
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com', testid: 'twitter' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com', testid: 'linkedin' },
  { icon: Github, label: 'GitHub', href: 'https://github.com', testid: 'github' },
  { icon: Youtube, label: 'YouTube', href: 'https://youtube.com', testid: 'youtube' },
];

const trustBadges = [
  { icon: Shield, label: 'SOC 2 Type II', testid: 'soc2' },
  { icon: Lock, label: 'GDPR Ready', testid: 'gdpr' },
  { icon: CheckCircle, label: 'ISO 27001 Aligned', testid: 'iso' },
];

const GrandFooter = () => {
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    toast.success("Thanks for subscribing — we'll keep you posted.");
    setEmail('');
  };

  return (
    <footer
      className="relative bg-[#0A0A0A] text-white overflow-hidden"
      data-testid="grand-footer"
    >
      {/* Subtle grain / ruby tint overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Giant background wordmark */}
      <div className="absolute -bottom-10 md:-bottom-20 left-0 right-0 pointer-events-none overflow-hidden">
        <div
          className="font-extrabold tracking-tighter leading-none whitespace-nowrap text-white/[0.03] select-none text-center"
          style={{
            fontFamily: "'Space Grotesk', 'Inter', sans-serif",
            fontSize: 'clamp(12rem, 26vw, 26rem)',
          }}
          aria-hidden="true"
        >
          EVALENTX
        </div>
      </div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8">
        {/* Top — Brand block + Newsletter (eyebrow row) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="pt-20 md:pt-28 pb-12 grid lg:grid-cols-12 gap-12 border-b border-white/10"
        >
          {/* Brand block */}
          <div className="lg:col-span-5" data-testid="footer-brand">
            <Logo inverted size="lg" />
            <p className="mt-6 text-[#A3A3A3] text-sm leading-relaxed max-w-md">
              The premier platform for hiring, evaluation, and verified developer talent —
              built for the next generation of teams who care about real skill.
            </p>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="mt-8 flex items-center gap-2 max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email for newsletter"
                className="flex-1 bg-white/[0.04] border border-white/10 text-white placeholder-[#525252] text-sm font-medium px-4 py-3 rounded-full focus:outline-none focus:border-[#B91C1C] transition-colors"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 bg-[#B91C1C] hover:bg-[#7F1D1D] text-white text-sm font-bold px-5 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(185,28,28,0.5)]"
                data-testid="footer-newsletter-submit"
              >
                Subscribe
                <ArrowRight size={14} />
              </button>
            </form>

            {/* Socials */}
            <div className="mt-8 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-[#A3A3A3] hover:text-white hover:border-[#B91C1C] hover:bg-[#B91C1C]/10 transition-all"
                  data-testid={`footer-social-${s.testid}`}
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* 5-col link grid */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-5 gap-8">
            {linkGroups.map((group) => (
              <div key={group.title}>
                <h4
                  className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#525252] mb-5"
                  data-testid={`footer-group-${group.title.toLowerCase()}`}
                >
                  {group.title}
                </h4>
                <ul className="space-y-3">
                  {group.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className="text-[#A3A3A3] hover:text-white transition-colors text-sm font-medium"
                        data-testid={`footer-link-${l.testid}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Trust badges */}
        <div className="py-8 border-b border-white/10 flex flex-wrap items-center gap-x-8 gap-y-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#525252]">
            Enterprise grade
          </span>
          {trustBadges.map((b, i) => (
            <div
              key={b.label}
              className="inline-flex items-center gap-2 text-[#A3A3A3] text-xs font-semibold"
              data-testid={`footer-trust-${b.testid}`}
            >
              <b.icon size={14} className="text-[#B91C1C]" />
              <span>{b.label}</span>
              {i < trustBadges.length - 1 && (
                <span className="ml-6 hidden md:inline-block w-px h-3 bg-white/10" />
              )}
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-[#525252]">
          <p data-testid="footer-copyright">© 2026 EvalentX, Inc. All rights reserved.</p>
          <p className="font-medium">Made with intent in India.</p>
          <div className="inline-flex items-center gap-2">
            <Globe size={12} />
            <span className="font-semibold">EN — India</span>
          </div>
        </div>

        {/* Spacer so the giant background wordmark has air below */}
        <div className="h-32 md:h-44" aria-hidden="true" />
      </div>
    </footer>
  );
};

export default GrandFooter;
